import type { ReactionList } from '@/types/feed.type'
import type { PaginateResponse } from '@/utils/paginate'

import { useState } from 'react'
import axios, { endpoints } from '@/lib/axios'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ThumbsUp, Heart, Laugh, Star, Frown, Angry } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { Card } from '../ui/card'
import { Skeleton } from '../ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'

// Enum สำหรับ ReactionType
const ReactionType = {
  LIKE: 'like',
  LOVE: 'love',
  HAHA: 'haha',
  WOW: 'wow',
  SAD: 'sad',
  ANGRY: 'angry',
}

interface ReactionsListDialogProps {
  feedId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function getReactionIcon(type: string, isActive: boolean) {
  const iconProps = { size: 16 }

  switch (type) {
    case 'like':
      return isActive ? (
        <ThumbsUp
          fill="currentColor"
          {...iconProps}
          className="text-blue-400"
        />
      ) : (
        <ThumbsUp {...iconProps} className="text-blue-400" />
      )
    case 'love':
      return isActive ? (
        <Heart fill="currentColor" {...iconProps} className="text-pink-500" />
      ) : (
        <Heart {...iconProps} className="text-pink-500" />
      )
    case 'haha':
      return isActive ? (
        <Laugh fill="#ffe278" {...iconProps} className="text-yellow-700" />
      ) : (
        <Laugh {...iconProps} className="text-yellow-400" />
      )
    case 'wow':
      return isActive ? (
        <Star fill="currentColor" {...iconProps} className="text-green-400" />
      ) : (
        <Star {...iconProps} className="text-green-400" />
      )
    case 'sad':
      return isActive ? (
        <Frown fill="#8EC5FF" {...iconProps} className="text-blue-800" />
      ) : (
        <Frown {...iconProps} className="text-blue-300" />
      )
    case 'angry':
      return isActive ? (
        <Angry fill="#ff9a52" {...iconProps} className="text-orange-800" />
      ) : (
        <Angry {...iconProps} className="text-orange-500" />
      )
    default:
      return <ThumbsUp {...iconProps} className="text-blue-400" />
  }
}

export default function ReactionsListDialog({
  feedId,
  open,
  onOpenChange,
}: ReactionsListDialogProps) {
  const [reactions, setReactions] = useState<ReactionList[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(true)
  const [reactionType, setReactionType] = useState('all')

  const fetchFeeds = async (pageNumber = 1, append = false) => {
    setLoading(pageNumber === 1)
    try {
      const { data } = await axios.get<PaginateResponse<ReactionList>>(
        // tab === 'timeline' ? endpoints.feeds.timeline : endpoints.feeds.main,
        endpoints.feeds.reactionsById(feedId),
        {
          params: {
            page: pageNumber,
            limit: 15,
            orderBy: 'createdAt:desc',
            ...(reactionType !== 'all' && { reactionType }),
          },
        },
      )

      setReactions((prev) => (append ? [...prev, ...data.items] : data.items))
      setHasMore(pageNumber * data.limit < data.total)
    } catch {
      // TODO: handle error (toast)
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  useDeepEffect(() => {
    if (open) {
      fetchFeeds(1, false)
    }
  }, [open, reactionType])

  const fetchMoreData = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchFeeds(nextPage, true)
    }
  }

  const reactionTabs = [
    {
      type: 'all',
      label: 'ทั้งหมด',
    },
    {
      type: ReactionType.LIKE,
      label: 'ถูกใจ',
      icon: getReactionIcon('like', false),
    },
    {
      type: ReactionType.LOVE,
      label: 'รักเลย',
      icon: getReactionIcon('love', false),
    },
    {
      type: ReactionType.HAHA,
      label: 'ขำ',
      icon: getReactionIcon('haha', false),
    },
    {
      type: ReactionType.WOW,
      label: 'ว้าว',
      icon: getReactionIcon('wow', false),
    },
    {
      type: ReactionType.SAD,
      label: 'เศร้า',
      icon: getReactionIcon('sad', false),
    },
    {
      type: ReactionType.ANGRY,
      label: 'โกรธ',
      icon: getReactionIcon('angry', false),
    },
  ]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby=""
        className="w-[95dvw] max-w-2xl px-2 pt-0 bg-white border border-gray-200"
      >
        <DialogHeader>
          <DialogTitle className="text-gray-900" />
        </DialogHeader>

        <div className="pt-4">
          <Tabs defaultValue={reactionTabs[0]?.type} className="w-full">
            <TabsList className="grid w-full grid-cols-7 bg-gray-100 border border-gray-200 h-full gap-2">
              {reactionTabs.map((reaction) => (
                <TabsTrigger
                  key={reaction.type}
                  value={reaction.type}
                  className="flex flex-col items-center gap-1 p-2 data-[state=active]:bg-white data-[state=active]:text-gray-900 text-gray-600 hover:text-gray-800"
                  onClick={() => {
                    setPage(1)
                    setReactionType(reaction.type)
                  }}
                >
                  {reaction?.type !== 'all' && reaction?.icon}
                  {reaction?.type === 'all' && (
                    <span className="text-xs">{reaction?.label}</span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>

            {reactionTabs.map((reaction) => (
              <TabsContent
                id="scrollableDiv"
                key={reaction.type}
                value={reaction.type}
                className="mt-4 p-4 border border-gray-200 rounded-xl max-h-[calc(100dvh-200px)] overflow-auto bg-white"
              >
                {loading ? (
                  <div className="max-w-lg mx-auto">
                    <div className="space-y-4">
                      {[...Array(4)].map((_, i) => (
                        <Skeleton
                          key={i}
                          className="h-20 w-full rounded-xl bg-gray-200"
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <InfiniteScroll
                      scrollableTarget="scrollableDiv"
                      dataLength={reactions.length}
                      next={fetchMoreData}
                      hasMore={hasMore}
                      loader={
                        <div className="text-center">
                          <div className="space-y-4">
                            {[...Array(3)].map((_, i) => (
                              <Skeleton
                                key={i}
                                className="h-20 w-full rounded-xl bg-gray-200"
                              />
                            ))}
                          </div>
                        </div>
                      }
                      endMessage={
                        <>
                          {/* {!!reactions.length && (
                            <div className="text-center py-4 text-gray-400">
                              <p>•</p>
                              <p>•</p>
                              <p>ไม่มีการถูกใจเพิ่มเติม</p>
                            </div>
                          )} */}
                        </>
                      }
                    >
                      {reactions.length === 0 ? (
                        <div className="flex flex-col py-8 gap-2 items-center justify-center">
                          <div className="text-center text-gray-400">•</div>
                          <div className="text-center text-gray-400">•</div>
                          <div className="text-center text-gray-400">
                            ยังไม่มีการถูกใจ
                          </div>
                        </div>
                      ) : (
                        reactions.map((item) => (
                          <ReactionCard key={item.id} data={item} />
                        ))
                      )}
                    </InfiniteScroll>
                  </>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const ReactionCard = ({ data }: { data: ReactionList }) => {
  const { user } = data
  const { imageUrl, displayName, firstName, lastName, email } = user
  const name =
    firstName && lastName ? `${firstName} ${lastName}` : displayName || email

  return (
    <Card className="mb-4 p-4 bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow relative">
      <div className="flex gap-4 items-center text-gray-800">
        <div className="relative">
          <Avatar className="size-12">
            <AvatarImage
              src={imageUrl}
              alt={name || ''}
              className="object-cover"
            />
            {name && (
              <AvatarFallback className="size-12 bg-gray-100 text-gray-600">
                {name.slice(0, 2)}
              </AvatarFallback>
            )}
          </Avatar>

          <Avatar className="absolute -bottom-[6px] -left-2 w-[27px] h-[27px] border-2 border-white bg-white shadow-sm">
            <AvatarFallback className="bg-gray-100">
              {getReactionIcon(data.reactionType, false)}
            </AvatarFallback>
          </Avatar>
        </div>

        <span className="font-medium">{name}</span>
      </div>
    </Card>
  )
}
